import { useRef } from "react";
import { KeyCode } from "src/utils/keys";
import styled from "styled-components";

import { Color, Flex, Icon, Text } from "src/elements";
import { useHoverClickable } from "src/utils/hoverClickable";
import { MenuOption } from "../store/types";
import { colorToRGBA } from "src/utils/colorToRgba";

const OptionElement = styled(Flex)<{ isHovered: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: space-between;
  padding: 6px;
  border-radius: 2px;
  cursor: pointer;
  user-select: none;

  background-color: ${({ isHovered }) => isHovered && Color.white};
`;

const OptionKeybind = styled(Flex)<{ isHovered: boolean }>`
  display: flex;
  align-items: center;
  background-color: ${({ isHovered }) =>
    isHovered ? colorToRGBA(Color.textMain, 0.1) : colorToRGBA(Color.white, 0.2)};
  padding: 1px 3px;
  border-radius: 2px;
  gap: 3px;
`;

interface Props {
  option: MenuOption;
  isSelected: boolean;
}

export const Option = ({ option: { label, keybind, icon, onClick }, isSelected }: Props) => {
  const refOption = useRef<HTMLDivElement>(null);
  const { isHovered } = useHoverClickable(refOption);

  const isActive = isHovered || isSelected;

  const preKey = `
    ${keybind?.ctrl ? "CTRL + " : ""}
    ${keybind?.shift ? "SHIFT + " : ""}
    ${keybind?.keyCode === KeyCode.DELETE ? "Del" : ""}
    ${keybind?.keyCode === KeyCode.BACKSPACE ? "BS" : ""}
    ${keybind?.keyCode === KeyCode.N ? "N" : ""}
    ${keybind?.keyCode === KeyCode.D ? "D" : ""}
    ${keybind?.keyCode === KeyCode.U ? "U" : ""}
    ${keybind?.keyCode === KeyCode.X ? "X" : ""}
    ${keybind?.keyCode === KeyCode.Y ? "Y" : ""}
    ${keybind?.keyCode === KeyCode.S ? "S" : ""}
    ${keybind?.keyCode === KeyCode.H ? "H" : ""}
    ${keybind?.keyCode === KeyCode.L ? "L" : ""}
    ${keybind?.keyCode === KeyCode.Q ? "Q" : ""}
    ${keybind?.keyCode === KeyCode.E ? "E" : ""}
    ${keybind?.keyCode === KeyCode.F ? "F" : ""}
  `;

  return (
    <OptionElement ref={refOption} isHovered={isActive} onClick={onClick}>
      {icon && <Icon name={icon.name} size={14} color={icon.color} />}

      <Text variant="caption1" color={(isActive && Color.textMain) || Color.white}>
        {label}
      </Text>

      {keybind && (
        <OptionKeybind isHovered={isActive}>
          {preKey && (
            <Text variant="caption1" color={(isActive && Color.textMain) || Color.white}>
              {preKey}
            </Text>
          )}

          <Icon
            name={
              (keybind.keyCode === KeyCode.UP && "arrowUp") ||
              (keybind.keyCode === KeyCode.DOWN && "arrowDown") ||
              (keybind.keyCode === KeyCode.LEFT && "arrowLeft") ||
              (keybind.keyCode === KeyCode.RIGHT && "arrowRight")
            }
            size={12}
            color={(isActive && Color.textMain) || Color.white}
          />
        </OptionKeybind>
      )}
    </OptionElement>
  );
};
